import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Row, Select, Skeleton, Space } from "antd";
import { DownOutlined } from "@ant-design/icons";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "common/button";

import GET_CURRENT_USER_INFO from "graphql/queries/GetCurrentUserInfo";
import GET_CURRENT_PROGRESS from "graphql/queries/GetCurrentProgress";
import GET_LANGUAGES from "graphql/queries/GetLanguages";

import screenSize from "utils/screenSize";
import { SelectValue } from "antd/lib/select";
import useBannerBackground from "hooks/useBannerBackground";
import { Language, Level } from "types";
import {
  filterActiveLevelsAndCurrentUserLevel,
  mapLanguagesByLevelId,
} from "utils/language";
import LanguageFilterCommon from "../language_filter";
import {
  LevelSelector,
  SelectorLabel,
  LanguageSelectorContainer,
  LevelSelectorContainer,
} from "./styles";

import "./MyProgress.css";

type MyProgressProps = {
  defaultLevelId?: number; // optional
  showViewDetailButton: boolean; // wherever to show the VIEW DETAILS button or not
};

const MyProgress = ({
  defaultLevelId,
  showViewDetailButton,
}: MyProgressProps) => {
  const { t } = useTranslation("my_progress");
  const navigate = useNavigate();
  const location = useLocation();

  // Data management

  // initialize as nil if not in params
  // if nil, query for current level from 'me'
  // const [levelId, setLevelId] = useState(params.levelId || 0);

  // get student's current level before querying for details
  // note this can be different from the selected level id from the dropdown
  const { data, loading } = useQuery(GET_CURRENT_USER_INFO, {
    fetchPolicy: "network-only",
  });

  const { data: languageList } = useQuery(GET_LANGUAGES);

  const currentLevelId = data?.me.currentLevel.id;

  const [selectedLevelId, setSelectedLevelId] = useState(defaultLevelId || 0);

  const languagesByLevelId = mapLanguagesByLevelId(languageList?.languages);
  const initialLanguageId = languagesByLevelId[selectedLevelId]?.id;
  const [currentLanguageId, setCurrentLanguageId] = useState<
    string | undefined
  >(initialLanguageId);

  if (data && !currentLanguageId && languageList) {
    const studyLanguageId = data.me.currentLanguage.id;
    if (defaultLevelId) {
      const studyLanguage = languageList.languages.find(
        (language: Language) => {
          return language.levels.some(
            (level: Level) => level.id.toString() === defaultLevelId.toString()
          );
        }
      );

      if (studyLanguage && studyLanguage.id)
        setCurrentLanguageId(studyLanguage.id);
    } else {
      setCurrentLanguageId(studyLanguageId);
    }
  }

  if (currentLevelId && !selectedLevelId) {
    setSelectedLevelId(currentLevelId);
  }

  const {
    loading: currentProgressLoading,
    data: currentProgressData,
    error: currentProgressError,
  } = useQuery(GET_CURRENT_PROGRESS, {
    fetchPolicy: "network-only",
    variables: {
      levelId: selectedLevelId,
    },
    skip: selectedLevelId === 0,
  });

  const studyLanguage = languageList?.languages.find((language: Language) => {
    return language.id === currentLanguageId;
  });

  const { backgroundImage } = useBannerBackground();

  const levelOptions = filterActiveLevelsAndCurrentUserLevel(
    data?.me?.currentLevel?.id,
    studyLanguage?.levels
  );

  // When default level id changes, update selected level id and current language id
  useEffect(() => {
    setSelectedLevelId(defaultLevelId || 0);

    // When user navigate to /my-level, set the current language id is as current user's language
    if (!defaultLevelId && data) {
      const studyLanguageId = data.me?.currentLanguage?.id;
      setCurrentLanguageId(studyLanguageId);
    }

    // if user navigate to /my-level/:id, set the current language id as the language of the selected level
    if (
      defaultLevelId &&
      languagesByLevelId[defaultLevelId]?.id !== currentLanguageId
    ) {
      setCurrentLanguageId(languagesByLevelId[defaultLevelId]?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLevelId, data]);

  // Side effects
  const handleSelectedLevelChange = (value: number) => {
    setSelectedLevelId(value);

    if (showViewDetailButton) {
      // do nothing if VIEW DETAILS button is shown.
      // users need to click on button to navigate
    } else {
      // immediately navigate to My Level page if VIEW DETAILS button is hidden
      navigate(`/my-level/${value}`);
    }
  };

  const handleSelectedLanguageChange = (value: string) => {
    const selectedLanguage = languageList.languages.find(
      (language: Language) => language.id === value
    );
    if (selectedLanguage.levels.length) {
      setCurrentLanguageId(value);

      // set selected active level to the first level of the selected language
      const activeLevel = selectedLanguage.levels.find(
        (level: Level) => level.active
      );
      if (activeLevel) {
        handleSelectedLevelChange(activeLevel.id);
      }
    } else {
      setCurrentLanguageId(currentLanguageId);
    }
  };
  const onProgressDetails = () => {
    navigate(`/my-level/${selectedLevelId}`);
  };
  const isBlueBackground = location.pathname.includes("/my-level");
  //
  // Renderers
  const renderProgressSection = () => {
    if (currentProgressError) {
      console.log(currentProgressError);
      return <div>Cannot retrieve progresss information for this level.</div>;
    }
    if (currentProgressLoading) {
      return (
        <Skeleton active className="progress-bar-skeleton" paragraph={false} />
      );
    }
    if (!currentProgressData) {
      return <div>Checking current level...</div>;
    }

    const { currentProgress } = currentProgressData;

    return (
      <>
        <div
          className="progress-bar base"
          style={{ marginTop: screenSize() === "desktop" ? 0 : 18 }}
        >
          <div
            className="studied"
            style={{ width: `${currentProgress.studied}%` }}
          />
          <div
            className="reviewed"
            style={{
              width: `${currentProgress.reviewed}%`,
            }}
          />
          <div
            className="reviewed-several"
            style={{
              width: `${currentProgress.reviewedSeveral}%`,
            }}
          />
          <div
            className="skipped"
            style={{ width: `${currentProgress.skipped}%` }}
          />
        </div>
        <div className="percentages">
          <span
            className="studied"
            style={{ left: `${currentProgress.studied}%` }}
          >
            {currentProgress.studied}%
          </span>
          <span
            className="reviewed"
            style={{ left: `${currentProgress.reviewed}%` }}
          >
            {currentProgress.reviewed}%
          </span>
          <span
            className="reviewed-several"
            style={{
              left: `${currentProgress.reviewedSeveral}%`,
            }}
          >
            {currentProgress.reviewedSeveral}%
          </span>
        </div>
      </>
    );
  };

  const renderRightBlock = (
    <div className="right-block">
      <Space
        direction={
          window.matchMedia("(max-width: 1024px)").matches
            ? "vertical"
            : "horizontal"
        }
        size="middle"
      >
        <LanguageSelectorContainer>
          {isBlueBackground ? (
            <>
              <SelectorLabel
                style={{
                  width: screenSize() === "mobile" ? 75 : "fit-content",
                }}
              >
                Language
              </SelectorLabel>
              <LanguageFilterCommon
                filterLanguageId={
                  currentLanguageId || languageList?.languages[0].id
                }
                onChange={(value: SelectValue) =>
                  handleSelectedLanguageChange(value as string)
                }
                languages={languageList?.languages}
                isLeftAlign={false}
                noFixedSizeParent
                dropIcon={<DownOutlined className="value-dropdown-icon" />}
              />
            </>
          ) : null}
        </LanguageSelectorContainer>

        <LevelSelectorContainer>
          {isBlueBackground ? (
            <SelectorLabel
              style={{ width: screenSize() === "mobile" ? 75 : "fit-content" }}
            >
              Level
            </SelectorLabel>
          ) : null}
          <LevelSelector
            value={selectedLevelId.toString()}
            optionFilterProp="children"
            onChange={(value: SelectValue) =>
              handleSelectedLevelChange(value as number)
            }
            filterOption={(input: string, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            getPopupContainer={(trigger) => trigger.parentNode}
            dropdownStyle={{ textAlign: "left" }}
            loading={loading}
          >
            {levelOptions.map((level: Level) => (
              <Select.Option
                value={level.id}
                key={level.id}
                disabled={!level.active}
              >
                {level.title}
              </Select.Option>
            ))}
          </LevelSelector>
        </LevelSelectorContainer>

        {showViewDetailButton && (
          <Button
            name="viewProgress"
            type="primary"
            onClick={onProgressDetails}
          >
            {t("PROGRESS DETAILS")}
          </Button>
        )}
      </Space>
    </div>
  );

  return (
    <div
      className={isBlueBackground ? "blue-background section" : "section"}
      style={isBlueBackground ? backgroundImage : {}}
    >
      <div className="section-header">
        <span className="section-title progress-title">
          {t(`MY {{ language }} PROGRESS`, {
            language: studyLanguage?.englishName.toUpperCase() || "",
          })}
        </span>
        {screenSize() === "desktop" && renderRightBlock}
      </div>
      {screenSize() === "mobile" && renderRightBlock}
      {renderProgressSection()}
      <Row className="labels">
        <Row className="labels-left-container">
          <div className="label reviewed-several">
            <div className="label-cube" />
            <span className="label-text">
              {t("Topics studied more than twice")}{" "}
            </span>
          </div>
          <div className="label reviewed">
            <div className="label-cube" />
            <span className="label-text">
              {t("Topics studied more than once")}
            </span>
          </div>
          <div className="label studied">
            <div className="label-cube" />
            <span className="label-text">{t("Topics studied")}</span>
          </div>
        </Row>
        <div className="label skipped right">
          <div className="label-cube" />
          <span className="label-text">{t("Topics skipped")}</span>
        </div>
      </Row>
    </div>
  );
};

export default MyProgress;
